import { useLayoutEffect, useRef, useState, type ElementType } from 'react';

import { cn } from '../lib/cn';

const SUBPIXEL_ROUNDING = 1;

interface IClippedTextProps {
  text: string;
  className?: string;
  as?: ElementType;
  lines?: 1 | 2;
}

// Measured: `title` carries the whole text only once the ellipsis actually cut something.
export const ClippedText = ({
  text,
  className,
  as: Tag = 'span',
  lines = 1,
}: IClippedTextProps) => {
  const ref = useRef<HTMLElement>(null);
  const [clipped, setClipped] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () =>
      setClipped(
        element.scrollWidth - element.clientWidth > SUBPIXEL_ROUNDING ||
          element.scrollHeight - element.clientHeight > SUBPIXEL_ROUNDING,
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);

  return (
    <Tag
      ref={ref}
      title={clipped ? text : undefined}
      className={cn(
        'min-w-0',
        // `whitespace-normal`: inside a truncating cell the inherited nowrap kept it on one line.
        lines === 1 ? 'truncate' : 'line-clamp-2 whitespace-normal wrap-anywhere',
        className,
      )}
    >
      {text}
    </Tag>
  );
};
